import { useMemo, useState } from "react";
import type { ContributorFeedbackGroup } from "@skills-marketplace/contracts/contract-8-feedback";
import { reasonLabel } from "../lib/feedback";
import { entryLabel, groupByReason } from "../lib/feedback-groups";
import ChevronIcon from "../icons/ChevronIcon";
import styles from "./FeedbackReasons.module.css";

interface Props {
  /**
   * Feedback as the API returns it — grouped per entry (#621). Re-pivoted onto the
   * reason-chip axis here (#744); reasons are still NEVER merged across entries.
   */
  groups: ContributorFeedbackGroup[];
  /** The family root this expand was requested for, used to shorten row labels. */
  parentInstallName: string;
}

function formatVotedAt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

/**
 * The contributor-facing feedback list for one plugin's family, grouped by reason
 * chip (#621, restructured by #744).
 *
 * Read-only by design: no reply, no threading, no moderation (#621 AC #2). Anonymous by
 * construction — ContributorFeedbackItem carries no voter field, so there is nothing
 * here to attribute to a person.
 *
 * Groups are count-sorted so the contributor's biggest pattern is the first thing they
 * see, replacing the chronological list that made them read every entry to notice what
 * was recurring. Every row still names the entry it was left on, so a comment on one
 * skill is never misread as a comment on the plugin — the no-merge guarantee moved from
 * the group level to the row level, it did not go away. Both directions are shown:
 * knowing why something wasn't useful is the actionable half.
 *
 * Groups start collapsed. With the counts visible on the heads, the collapsed state IS
 * the summary #744 asks for — expanding is for reading the notes behind a pattern the
 * contributor has already spotted.
 */
export default function FeedbackReasons({ groups, parentInstallName }: Props) {
  const reasonGroups = useMemo(() => groupByReason(groups), [groups]);
  // The entry column only informs when a row COULD have come from a different entry.
  // A family whose single group is the parent would otherwise repeat "the plugin
  // itself" on every row — the same repetition entryLabel exists to remove. Not keyed
  // on `groups.length > 1`: a lone SKILL group still needs its slug, which is
  // informative.
  const showEntry = !(groups.length === 1 && groups[0]?.installName === parentInstallName);
  // Keyed by reason code ("" for the synthetic no-chip group) rather than by index, so
  // an open group stays open across a refetch that reorders the list.
  const [openCodes, setOpenCodes] = useState<ReadonlySet<string>>(new Set());

  function toggle(key: string) {
    setOpenCodes((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }

  return (
    <div className={styles.wrap}>
      {reasonGroups.map((group) => {
        const key = group.code ?? "";
        const isOpen = openCodes.has(key);
        return (
          <section key={key} className={styles.group}>
            <h4 className={styles.groupHeading}>
              <button
                type="button"
                className={styles.groupHead}
                aria-expanded={isOpen}
                onClick={() => toggle(key)}
              >
                {/*
                  The shared disclosure chevron the install/download accordions use.
                  Without it the head is visually identical to the non-interactive row
                  chips below, and since groups start collapsed it is the only route to
                  every written reason on the dashboard — a hover tint is not enough of
                  a cue. aria-hidden, so the accessible name is unchanged.
                */}
                <ChevronIcon expanded={isOpen} />
                <span className={styles.chip}>{group.label}</span>
                <span className={styles.count}>{group.items.length}</span>
              </button>
            </h4>
            {isOpen && (
              <ul className={styles.items}>
                {group.items.map(({ installName, item }, i) => (
                  <li key={i} className={styles.item}>
                    <span
                      className={item.direction === "up" ? styles.up : styles.down}
                      aria-label={item.direction === "up" ? "Found useful" : "Not useful"}
                    >
                      {item.direction === "up" ? "Useful" : "Not useful"}
                    </span>
                    {showEntry && (
                      <span className={styles.entry}>
                        {entryLabel(installName, parentInstallName)}
                      </span>
                    )}
                    {/*
                      The group's own chip is the heading, so only the item's OTHER chips
                      add anything here — they say what else this one piece of feedback
                      said, which is the context that a chip-axis view would otherwise
                      lose.
                    */}
                    {item.reasonCodes.length > 1 && (
                      <span className={styles.chips}>
                        {item.reasonCodes
                          .filter((code) => code !== group.code)
                          .map((code) => (
                            <span key={code} className={styles.chip}>
                              {reasonLabel(code)}
                            </span>
                          ))}
                      </span>
                    )}
                    {/* Untrusted user input (contract-8): render as text, never as HTML. */}
                    {item.reasonText && <p className={styles.text}>{item.reasonText}</p>}
                    <span className={styles.date}>{formatVotedAt(item.votedAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
